// src/components/common/sidebar/Sidebar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  Collapse,
  styled
} from '@mui/material';
import {
  Home,
  Users,
  Calendar,
  FileText,
  Settings,
  LogOut,
  PlusCircle,
  ChevronRight,
  ChevronDown,
  User,
  CheckSquare,
  Hand,
  Headset
} from 'lucide-react';
import { HEALTH_GREEN } from '../../../components/Login/utils/mockData';
import { useSubscription } from '../../../contexts/context/SubscriptionContext'; // Importar el contexto de suscripción

const DrawerWidth = 240;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DrawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DrawerWidth,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF',
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    border: 'none',
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  justifyContent: 'center',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
}));

const MenuListItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(1),
  backgroundColor: active
    ? theme.palette.mode === 'dark'
      ? 'rgba(76, 175, 80, 0.15)'
      : 'rgba(76, 175, 80, 0.1)'
    : 'transparent',
  color: active ? HEALTH_GREEN : theme.palette.text.primary,
  cursor: 'pointer', // Agregamos el cursor pointer aquí
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.03)',
  },
  '& .MuiListItemIcon-root': {
    color: active ? HEALTH_GREEN : theme.palette.text.secondary,
  }
}));

const SubMenuListItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(0.5),
  marginLeft: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  backgroundColor: active
    ? theme.palette.mode === 'dark'
      ? 'rgba(76, 175, 80, 0.15)'
      : 'rgba(76, 175, 80, 0.1)'
    : 'transparent',
  color: active ? HEALTH_GREEN : theme.palette.text.primary,
  cursor: 'pointer', // Agregamos el cursor pointer aquí
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.03)',
  },
  '& .MuiListItemIcon-root': {
    color: active ? HEALTH_GREEN : theme.palette.text.secondary,
    minWidth: '30px',
  }
}));

// Componente para el dot verde de notificación
const NotificationDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: HEALTH_GREEN,
  position: 'absolute',
  top: 8,
  right: 12,
  boxShadow: `0 0 0 2px ${theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF'}`,
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: `0 0 0 0 ${HEALTH_GREEN}40`,
    },
    '70%': {
      boxShadow: `0 0 0 6px ${HEALTH_GREEN}00`,
    },
    '100%': {
      boxShadow: `0 0 0 0 ${HEALTH_GREEN}00`,
    },
  },
}));

const Sidebar = ({ 
  open, 
  isMobile, 
  onClose, 
  onNewPatient, 
  onLogout,
  activeSection = 'dashboard',
  user = {} // Mantenemos user como prop con valor por defecto
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [patientsOpen, setPatientsOpen] = useState(activeSection === 'patients');
  const { hasSubscription } = useSubscription(); // Usar el contexto de suscripción
  
  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const togglePatientsMenu = () => {
    setPatientsOpen(!patientsOpen);
  };

  const handleAllPatientsClick = () => {
    navigate('/patients');
    if (isMobile) {
      onClose();
    }
  };

  const handleNewPatientClick = () => {
    onNewPatient();
    if (isMobile) {
      onClose();
    }
  };
  
  // Determinar si la sección de pacientes está activa
  const isPatientsActive = activeSection === 'patients' || location.pathname.startsWith('/patients');
  
  // Determinar qué subsección de pacientes está activa
  const isAllPatientsActive = location.pathname === '/patients';
  
  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <DrawerHeader>
        <Box sx={{ textAlign: 'center', width: '100%' }}>
            <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
            }}>
            AGT Health
            <Box
                component="span"
                sx={{ 
                color: HEALTH_GREEN, 
                fontSize: '20px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-8px'
                }}
            >
                +
            </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary">
            Plataforma Profesional
            </Typography>
        </Box>
      </DrawerHeader>
      
      <List sx={{ px: 2, pt: 3 }}>
        {/* Dashboard */}
        <MenuListItem 
          button 
          active={activeSection === 'dashboard' || location.pathname === '/dashboard' ? 1 : 0}
          onClick={() => handleMenuItemClick('/dashboard')}
        >
          <ListItemIcon><Home size={20} /></ListItemIcon>
          <ListItemText primary="Dashboard" />
        </MenuListItem>

        {/* Mi AGT - Solo para usuarios con suscripción */}
        {hasSubscription && (
          <MenuListItem 
            button 
            active={activeSection === 'my-agt' || location.pathname === '/my-agt' ? 1 : 0}
            onClick={() => handleMenuItemClick('/my-agt')}
            sx={{ position: 'relative' }}
          >
            <ListItemIcon><Hand size={20} /></ListItemIcon>
            <ListItemText primary="Mi AGT" />
            <NotificationDot />
          </MenuListItem>
        )}
        
        {/* Elemento de Pacientes con submenú desplegable */}
        <MenuListItem 
          button 
          active={isPatientsActive ? 1 : 0}
          onClick={togglePatientsMenu}
        >
          <ListItemIcon><Users size={20} /></ListItemIcon>
          <ListItemText primary="Pacientes" />
          {patientsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </MenuListItem>
        
        {/* Submenú de Pacientes */}
        <Collapse in={patientsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <SubMenuListItem
              button
              active={isAllPatientsActive ? 1 : 0}
              onClick={handleAllPatientsClick}
            >
              <ListItemIcon><User size={18} /></ListItemIcon>
              <ListItemText primary="Todos los Pacientes" />
            </SubMenuListItem>
            <SubMenuListItem
              button
              onClick={handleNewPatientClick}
            >
              <ListItemIcon><PlusCircle size={18} /></ListItemIcon>
              <ListItemText primary="Nuevo Paciente" />
            </SubMenuListItem>
          </List>
        </Collapse>
        
        {/* Citas */}
        <MenuListItem 
          button 
          active={activeSection === 'appointments' || location.pathname === '/appointments' ? 1 : 0}
          onClick={() => handleMenuItemClick('/appointments')}
        >
          <ListItemIcon><Calendar size={20} /></ListItemIcon>
          <ListItemText primary="Citas" />
        </MenuListItem>
        
        {/* Tareas - Nuevo ítem */}
        <MenuListItem 
          button 
          active={activeSection === 'tasks' || location.pathname === '/tasks' ? 1 : 0}
          onClick={() => handleMenuItemClick('/tasks')}
        >
          <ListItemIcon><CheckSquare size={20} /></ListItemIcon>
          <ListItemText primary="Tareas" />
        </MenuListItem>
        
        {/* Registros */}
        <MenuListItem 
          button 
          active={activeSection === 'records' || location.pathname === '/records' ? 1 : 0}
          onClick={() => handleMenuItemClick('/records')}
        >
          <ListItemIcon><FileText size={20} /></ListItemIcon>
          <ListItemText primary="Registros" />
        </MenuListItem>

        {/* Intercom - Soporte */}
        <MenuListItem 
          button 
          active={activeSection === 'intercom' || location.pathname === '/intercom' ? 1 : 0}
          onClick={() => handleMenuItemClick('/intercom')}
        >
          <ListItemIcon><Headset size={20} /></ListItemIcon>
          <ListItemText primary="Soporte" />
        </MenuListItem>
           
        {/* Configuración */}
        <MenuListItem 
          button 
          active={activeSection === 'settings' || location.pathname === '/settings' ? 1 : 0}
          onClick={() => handleMenuItemClick('/settings')}
        >
          <ListItemIcon><Settings size={20} /></ListItemIcon>
          <ListItemText primary="Configuración" />
        </MenuListItem>
      </List>
      
      <DrawerFooter>
        <Button 
          fullWidth 
          variant="outlined" 
          color="inherit"
          startIcon={<LogOut size={18} />}
          onClick={onLogout}
          sx={{ 
            borderRadius: 3,
            textTransform: 'none'
          }}
        >
          Cerrar sesión
        </Button>
      </DrawerFooter>
    </StyledDrawer>
  );
};

export default Sidebar;